import { Box, StackLayout, UtilityText } from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { MainPageContainer } from '@leagueplatform/web-common-components';
import { useLOB, useLocale } from '@scfhp-web/shared-module';
import { EVENT_NAME, trackAnalyticsEvent } from '@leagueplatform/analytics';
import { openInNewTab } from 'utils/open-in-new-tab';
import { LinkComponent } from 'components/custom-link.component';
import { APP_STORE_LINK, GOOGLE_PLAY_LINK } from 'commons/constants';
import googleBadge from 'assets/google-badge.svg';
import appleBadge from 'assets/apple-badge.svg';
import {
  FooterLinkProps,
  footerLinksConfig,
  footerSubLinksConfig,
} from '../../utils/footer-link-list';
import { AppStoreLink } from './app-store-link.component';

export const FooterComponent = () => {
  const { language } = useLocale();
  const lob = useLOB();
  const linkItems = footerLinksConfig(lob, language);
  const subLinkItems = footerSubLinksConfig();
  const { formatMessage } = useIntl();
  const currentYear = new Date().getFullYear();

  const handleAppStoreClick = () => {
    openInNewTab(APP_STORE_LINK);
    trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
      product_area: 'Footer',
      sub_product_area: 'app download',
      screen_name: 'Footer',
      detail: 'navigate to app store',
      content: { name: 'App Store' },
    });
  };

  const handleGooglePlayClick = () => {
    openInNewTab(GOOGLE_PLAY_LINK);
    trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
      product_area: 'Footer',
      sub_product_area: 'app download',
      screen_name: 'Footer',
      detail: 'navigate to app store',
      content: { name: 'Google Play' },
    });
  };

  return (
    <StackLayout
      css={{
        background: '$decorativeBrandPrimaryPastel',
        flexWrap: 'wrap',
        paddingTop: '$three',
        paddingBottom: '$twoAndHalf',
        marginTop: '$three',
        '@laptop': {
          flexWrap: 'nowrap',
        },
        '@desktop': {
          flexWrap: 'nowrap',
        },
      }}
    >
      <MainPageContainer
        paddingX={{ _: 'oneAndHalf', phone: 'two' }}
        width="100%"
      >
        <StackLayout
          orientation="horizontal"
          verticalAlignment="center"
          horizontalAlignment="spaceBetween"
          css={{
            '@mobile': {
              flexDirection: 'column',
              alignItems: 'center',
            },
            '@laptop': {
              flexDirection: 'row',
            },
          }}
        >
          <StackLayout
            orientation="vertical"
            verticalAlignment="center"
            horizontalAlignment="center"
            css={{
              flexShrink: 0,
              flex: 0,
              marginRight: '$oneAndHalf',
            }}
          >
            <AppStoreLink
              href={APP_STORE_LINK}
              imgSrc={appleBadge}
              altText="Apple Store"
              imgStyle={{ width: '161px', height: '57px' }}
              onClick={handleAppStoreClick}
            />
            <AppStoreLink
              href={GOOGLE_PLAY_LINK}
              imgSrc={googleBadge}
              altText="Google Play Store"
              imgStyle={{ width: '161px', height: '57px' }}
              onClick={handleGooglePlayClick}
            />
          </StackLayout>

          <StackLayout
            orientation="vertical"
            verticalAlignment="center"
            css={{
              flexGrow: 1,
              '@mobile': {
                marginTop: '$two',
              },
              '@laptop': {
                flexDirection: 'row',
                marginTop: '0',
              },
            }}
          >
            <Box>
              {linkItems?.map((item: FooterLinkProps) => (
                <LinkComponent key={item.text} {...item} />
              ))}
            </Box>
            <Box
              css={{
                margin: '$one 0 $oneAndHalf 0',
              }}
            >
              {subLinkItems?.map((item: FooterLinkProps) => (
                <LinkComponent key={item.text} {...item} />
              ))}
            </Box>
            <UtilityText size="xs" css={{ marginLeft: '$one' }}>
              {formatMessage(
                { id: 'STR_COPYRIGHT_TEXT' },
                { year: currentYear },
              )}
            </UtilityText>
          </StackLayout>
        </StackLayout>
      </MainPageContainer>
    </StackLayout>
  );
};
